import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingBag,
  Clock,
  UtensilsCrossed,
  BarChart3,
  Tag,
  Wallet,
  Store,
  AlertCircle,
  Star,
  Phone,
  Printer,
  Users,
  UserCog,
  MessageCircle,
  Calendar,
  Database,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Gift,
  CalendarClock,
  TrendingUp,
  LayoutGrid,
  Monitor,
} from 'lucide-react';
import { cn } from '../../../utils/cn';

const Sidebar = ({ activeView, onViewChange, isCollapsed, onToggleCollapse }) => {
  const [expandedMenus, setExpandedMenus] = useState({});

  const toggleSubMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Menu structure with Zomato-like organization
  const menuItems = [
    {
      id: 'orders',
      label: 'Orders',
      icon: ShoppingBag,
    },
    {
      id: 'menu',
      label: 'Menu',
      icon: UtensilsCrossed,
      subItems: [
        { id: 'menu-editor', label: 'Menu Editor' },
        { id: 'inventory', label: 'Manage Inventory' },
        { id: 'restaurant-info', label: 'Restaurant Info' },
      ],
    },
    {
      id: 'order-history',
      label: 'Order History',
      icon: Clock,
    },
    {
      id: 'reporting',
      label: 'Reporting',
      icon: BarChart3,
      subItems: [
        { id: 'live-tracking', label: 'Live Tracking' },
        { id: 'business-reports', label: 'Business Reports' },
      ],
    },
    {
      id: 'advanced-analytics',
      label: 'Advanced Analytics',
      icon: TrendingUp,
      badge: 'New',
      subItems: [
        { id: 'analytics/sales-forecasting', label: 'Sales Forecasting' },
        { id: 'analytics/customer-segmentation', label: 'Customer Segmentation' },
        { id: 'analytics/menu-engineering', label: 'Menu Engineering' },
        { id: 'analytics/staff-performance', label: 'Staff Performance' },
        { id: 'analytics/predictive-insights', label: 'Predictive Insights' },
      ],
    },
    {
      id: 'offers',
      label: 'Offers',
      icon: Tag,
      subItems: [
        { id: 'create-offers', label: 'Create Offers' },
        { id: 'track-offers', label: 'Track Offers' },
      ],
    },
    {
      id: 'finance',
      label: 'Finance',
      icon: Wallet,
      subItems: [
        { id: 'outlet-finance-reports', label: 'Outlet Finance Reports' },
        { id: 'payouts', label: 'Payouts' },
        { id: 'transactions', label: 'Transactions' },
        { id: 'invoices', label: 'Invoices & Taxes' },
      ],
    },
    {
      id: 'outlets',
      label: 'Outlets',
      icon: Store,
    },
    {
      id: 'pos-management',
      label: 'POS Management',
      icon: Monitor,
      badge: 'Waiter',
      subItems: [
        { id: 'tables', label: 'Table Management' },
        { id: 'terminals', label: 'Terminal Management' },
        { id: 'pos-settings', label: 'POS Settings' },
      ],
    },
    {
      id: 'complaints',
      label: 'Customer Complaints',
      icon: AlertCircle,
    },
    {
      id: 'reviews',
      label: 'Reviews',
      icon: Star,
    },
    { type: 'divider' },
    // Custom features (Your existing features)
    {
      id: 'phone-order',
      label: 'Phone Order',
      icon: Phone,
      badge: 'Custom',
    },
    {
      id: 'thermal-printer',
      label: 'Thermal Printer',
      icon: Printer,
      badge: 'Custom',
    },
    {
      id: 'customers',
      label: 'Customers',
      icon: Users,
    },
    {
      id: 'users',
      label: 'Users & Staff',
      icon: UserCog,
    },
    {
      id: 'user-sync',
      label: 'User Sync',
      icon: UserCog,
    },
    {
      id: 'chat',
      label: 'Chat',
      icon: MessageCircle,
    },
    {
      id: 'calendar',
      label: 'Calendar',
      icon: Calendar,
    },
    {
      id: 'loyalty',
      label: 'Loyalty Program',
      icon: Gift,
      badge: 'New',
    },
    {
      id: 'scheduled-orders',
      label: 'Scheduled Orders',
      icon: CalendarClock,
      badge: 'New',
    },
    {
      id: 'redemption-history',
      label: 'Redemption History',
      icon: Gift,
    },
    {
      id: 'data-management',
      label: 'Data Management',
      icon: Database,
    },
    { type: 'divider' },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
    },
  ];

  const renderMenuItem = (item, index) => {
    if (item.type === 'divider') {
      return (
        <motion.div
          key={`divider-${index}`}
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: index * 0.02 }}
          className="my-2 h-px bg-gradient-to-r from-transparent via-border-light to-transparent"
        />
      );
    }

    const Icon = item.icon;
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedMenus[item.id];
    const isActive = activeView === item.id ||
      (hasSubItems && item.subItems.some(sub => activeView === sub.id));

    const handleClick = () => {
      if (hasSubItems && !isCollapsed) {
        toggleSubMenu(item.id);
      } else {
        onViewChange(item.id);
      }
    };

    return (
      <motion.div
        key={item.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.02 }}
        className="mb-0.5"
      >
        <motion.button
          className={cn(
            'group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg',
            'transition-all duration-200',
            'hover:bg-bg-tertiary',
            isActive && [
              'bg-gradient-to-r from-secondary/10 to-transparent',
              'border-l-4 border-secondary',
              'text-secondary',
            ],
            !isActive && 'text-text-secondary hover:text-text-primary'
          )}
          onClick={handleClick}
          title={isCollapsed ? item.label : undefined}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {/* Active indicator */}
          {isActive && (
            <motion.div
              layoutId="activeIndicator"
              className="absolute inset-0 bg-gradient-to-r from-secondary/5 to-transparent rounded-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
            />
          )}

          {/* Icon with hover effect */}
          <motion.span
            className={cn(
              'relative z-10 flex-shrink-0 transition-transform duration-200',
              isActive && 'text-secondary',
              'group-hover:scale-110'
            )}
            whileHover={{ rotate: [0, -10, 10, -10, 0] }}
            transition={{ duration: 0.5 }}
          >
            <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
          </motion.span>

          {!isCollapsed && (
            <>
              <span className={cn(
                'relative z-10 flex-1 text-left text-sm font-medium transition-colors duration-200',
                isActive && 'font-semibold'
              )}>
                {item.label}
              </span>

              {/* Badge */}
              {item.badge && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="relative z-10 px-2 py-0.5 text-xs font-semibold bg-gradient-to-r from-accent to-accent-dark text-primary-dark rounded-full"
                >
                  <Sparkles size={10} className="inline mr-1" />
                  {item.badge}
                </motion.span>
              )}

              {/* Chevron for submenu */}
              {hasSubItems && (
                <motion.span
                  className="relative z-10"
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown size={16} />
                </motion.span>
              )}
            </>
          )}

          {/* Hover glow effect */}
          {isActive && (
            <div className="absolute inset-0 rounded-lg bg-secondary/5 blur-sm -z-10" />
          )}
        </motion.button>

        {/* Sub-menu with animation */}
        <AnimatePresence>
          {hasSubItems && !isCollapsed && isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="ml-4 mt-1 space-y-0.5 overflow-hidden"
            >
              {item.subItems.map((subItem, subIndex) => (
                <motion.button
                  key={subItem.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: subIndex * 0.05 }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm',
                    'transition-all duration-150',
                    'hover:bg-bg-tertiary hover:translate-x-1',
                    activeView === subItem.id
                      ? 'bg-bg-tertiary text-secondary font-medium'
                      : 'text-text-tertiary hover:text-text-primary'
                  )}
                  onClick={() => onViewChange(subItem.id)}
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Active dot */}
                  <span className={cn(
                    'w-1.5 h-1.5 rounded-full transition-colors duration-200',
                    activeView === subItem.id ? 'bg-secondary' : 'bg-border-light'
                  )} />
                  {subItem.label}
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <motion.aside
      initial={{ x: -100 }}
      animate={{ x: 0 }}
      className={cn(
        'fixed left-0 top-0 bottom-0 z-fixed',
        'flex flex-col',
        'bg-gradient-to-b from-bg-primary via-bg-primary to-bg-secondary',
        'border-r border-border-light',
        'shadow-2xl',
        'transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-20' : 'w-64'
      )}
    >
      {/* Logo Header */}
      <div className={cn(
        'flex items-center justify-between',
        'min-h-[80px] border-b border-border-light',
        'bg-gradient-to-r from-primary-dark to-bg-primary',
        isCollapsed ? 'px-4 justify-center' : 'px-6'
      )}>
        <motion.div
          className="flex items-center gap-2 overflow-hidden"
          whileHover={{ scale: 1.05 }}
        >
          {isCollapsed ? (
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-secondary to-accent blur-md opacity-50" />
              <span className="relative text-2xl font-extrabold bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent">
                OZ
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-xl font-bold text-text-primary">Odisha</span>
              <span className="text-xl font-bold bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent">
                Zaika
              </span>
            </div>
          )}
        </motion.div>

        {!isCollapsed && (
          <motion.button
            className={cn(
              'flex items-center justify-center',
              'w-8 h-8 rounded-lg',
              'text-text-tertiary hover:text-text-primary',
              'hover:bg-bg-tertiary',
              'transition-all duration-200'
            )}
            onClick={onToggleCollapse}
            title="Collapse sidebar"
            whileHover={{ scale: 1.1, rotate: -90 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronLeft size={18} />
          </motion.button>
        )}
      </div>

      {/* Expand button (when collapsed) */}
      {isCollapsed && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={cn(
            'absolute -right-3 top-24 z-50',
            'flex items-center justify-center',
            'w-6 h-6 rounded-full',
            'bg-gradient-to-r from-secondary to-accent',
            'text-white shadow-lg',
            'hover:shadow-glow',
            'transition-all duration-200'
          )}
          onClick={onToggleCollapse}
          title="Expand sidebar"
          whileHover={{ scale: 1.2 }}
          whileTap={{ scale: 0.9 }}
        >
          <ChevronRight size={14} />
        </motion.button>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden p-3 custom-scrollbar">
        {menuItems.map((item, index) => renderMenuItem(item, index))}
      </nav>

      {/* Footer */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="border-t border-border-light p-4 bg-gradient-to-t from-bg-secondary to-transparent"
          >
            <div className="text-center">
              <p className="text-xs font-semibold text-text-primary mb-0.5">
                Odisha Zaika Admin
              </p>
              <p className="text-xs text-text-tertiary flex items-center justify-center gap-1">
                <span className="w-1.5 h-1.5 bg-success rounded-full animate-pulse" />
                Version 2.0.0
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 107, 53, 0.3);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 107, 53, 0.5);
        }
      `}</style>
    </motion.aside>
  );
};

export default Sidebar;
